/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import { useHookstate, type State } from '@hookstate/core'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Collapse,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import React from 'react'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import { useLang } from '~/hooks/useLang'
import { buildReportTypeOptions } from './reportTypeOptions'
import FilterAutocompleteStations from '~/components/FilterAutocompleteStations'

interface AuditUserActionsReportFiltersProps {
  readonly hidden?: boolean
  readonly labelButton?: State<string>
  readonly loadingButton: State<boolean>
  readonly resetValues?: State<boolean>
  readonly focusChange?: State<boolean>
  readonly userId?: State<number | null>
  readonly initialPeriod: State<string>
  readonly onPeriodChange: (value: string) => void
  readonly onStartDateChange: (value: string | null) => void
  readonly onFinishDateChange: (value: string | null) => void
  readonly clearFilters: () => void
  readonly searchButton?: () => void
  readonly stationId: State<number | null>
  readonly reportType: State<string | null>
}

export default function AuditUserActionsReportFilters({
  hidden,
  labelButton,
  loadingButton,
  userId,
  resetValues,
  focusChange,
  initialPeriod,
  onStartDateChange,
  onFinishDateChange,
  onPeriodChange,
  clearFilters,
  searchButton,
  stationId,
  reportType,
}: AuditUserActionsReportFiltersProps) {
  const { translate } = useLang()

  const reportTypeOptions = buildReportTypeOptions(translate)

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
      >
        <Grid container direction={'column'} display={'flex'} gap={1}>
          <Grid container direction={'row'} flexWrap={'wrap'}>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="report-type-label">
                  {translate('Report')}
                </InputLabel>
                <Select
                  labelId="report-type-label"
                  id="report-type"
                  value={reportType.get() || ''}
                  label={translate('Report')}
                  onChange={(item) => reportType.set(item.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 200,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    },
                  }}
                  endAdornment={
                    reportType.get() ? (
                      <IconButton
                        size="small"
                        onClick={() => reportType.set(null)}
                        sx={{ marginRight: 2 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  {reportTypeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteStations
                onStationsChange={(value) =>
                  stationId?.set(value as number | null)
                }
                stationsId={stationId?.get() ? stationId.get() : null}
                disabled={false}
                single
              />
            </Grid>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteFetch
                label={translate('User')}
                urlFetch={'/user/get'}
                dataRequest={'user'}
                size={'small'}
                disabled={false}
                onFetchChange={(user: number | null) => userId?.set(user)}
                resetValues={resetValues?.get()}
                focusChange={focusChange}
                valueId={userId?.get() ? Number(userId?.get()) : undefined}
              />
            </Grid>
            <FilterSelectPeriod
              onStartDateChange={(value) =>
                onStartDateChange(value?.toJSON() ?? null)
              }
              onFinishDateChange={(value) =>
                onFinishDateChange(value?.toJSON() ?? null)
              }
              onPeriodChange={onPeriodChange}
              period={initialPeriod.get()}
              xsDateBetween={2}
              xsPeriod={2}
              hidePeriod={false}
              marginDateBetween={0}
              disableFuture={false}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction={'row'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          pt: 4,
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            clearFilters()
            stationId.set(null)
          }}
        >
          {translate('Clear Filters')}
        </Button>
        <LoadingButton
          loading={loadingButton.get()}
          variant="contained"
          onClick={searchButton}
        >
          {labelButton?.get()}
        </LoadingButton>
      </Grid>
    </Collapse>
  )
}
